import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

export interface FilterOption<T extends string | number> {
  value: T;
  label: string;
  /** Shown at the end of the row; options with 0 are dimmed */
  count?: number;
}

interface FilterSelectProps<T extends string | number> {
  value: T;
  options: FilterOption<T>[];
  onChange: (value: T) => void;
  ariaLabel: string;
  title?: string;
  /** Tint the trigger when a non-default value is picked */
  highlight?: boolean;
  /** Which edge the menu lines up with */
  align?: 'left' | 'right';
  className?: string;
}

/**
 * Dropdown in the site's dark/fuchsia style. Native <select> menus are drawn by the OS
 * (white on Windows), so the filters use this instead.
 */
export function FilterSelect<T extends string | number>({
  value,
  options,
  onChange,
  ariaLabel,
  title,
  highlight,
  align = 'left',
  className = '',
}: FilterSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const current = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={`relative min-w-0 ${className}`}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={title}
        onClick={() => setOpen((v) => !v)}
        className={`w-full h-8 sm:h-9 pl-3 sm:pl-3.5 pr-7 sm:pr-8 rounded-full border text-left text-[12px] sm:text-[13px] font-medium truncate transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-500/40 ${
          open
            ? 'bg-fuchsia-500/10 border-fuchsia-500/60 text-white'
            : highlight
              ? 'bg-fuchsia-500/10 border-fuchsia-500/50 text-fuchsia-100'
              : 'bg-white/[0.03] border-white/[0.07] text-slate-200 hover:bg-white/[0.06]'
        }`}
      >
        {current?.label ?? String(value)}
        <ChevronDown
          className={`w-3.5 h-3.5 absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 pointer-events-none transition-transform ${
            open ? 'rotate-180 text-fuchsia-300' : 'text-slate-500'
          }`}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={ariaLabel}
          className={`absolute z-30 top-full mt-1.5 min-w-full w-max max-w-[80vw] max-h-72 overflow-y-auto p-1 rounded-2xl bg-[#11132a]/95 backdrop-blur-md border border-white/10 shadow-2xl shadow-black/60 ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {options.map((o) => {
            const selected = o.value === value;
            return (
              <li key={String(o.value)} role="option" aria-selected={selected}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  className={`w-full h-9 px-3 rounded-xl flex items-center justify-between gap-3 text-left text-[13px] whitespace-nowrap transition-colors ${
                    selected
                      ? 'bg-fuchsia-500/20 text-white font-semibold'
                      : o.count === 0
                        ? 'text-slate-500 hover:bg-white/[0.04]'
                        : 'text-slate-300 hover:bg-white/[0.06] hover:text-white'
                  }`}
                >
                  <span>{o.label}</span>
                  <span className="flex items-center gap-1.5 shrink-0">
                    {o.count !== undefined && (
                      <span
                        className={`min-w-[22px] h-5 px-1.5 rounded-full grid place-items-center text-[11px] font-semibold ${
                          selected ? 'bg-fuchsia-400/25 text-fuchsia-100' : 'bg-white/[0.06] text-slate-400'
                        }`}
                      >
                        {o.count}
                      </span>
                    )}
                    {selected ? (
                      <Check className="w-3.5 h-3.5 text-fuchsia-300" />
                    ) : (
                      o.count !== undefined && <span className="w-3.5" />
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
